<template>
  <div class="order-history-page">
    <!-- 头部（与隐私政策/协议风格一致） -->
    <div class="header">
      <div class="back-btn" @click="goBack" role="button" :aria-label="$t('common.back')">
        <span class="back-icon">‹</span>
      </div>
      <div class="header-title">{{ $t('orderHistory.title') }}</div>
    </div>

    <!-- 主要内容区 -->
    <div class="main-content">
      <template v-if="orderList.length > 0">
        <div
          v-for="order in orderList"
          :key="order.id"
          class="order-card"
        >
          <!-- 左侧装饰条 -->
          <div class="order-status-bar" :class="getStatusClass(order.status)"></div>
          
          <div class="order-info">
            <!-- 第一行：商品名 + 金额 -->
            <div class="order-header">
              <span class="product-name"></span>
              <span class="order-amount">
                {{ order.amount.toFixed(2) }}
              </span>
            </div>
            
            <!-- 第二行：订单号（部分隐藏） -->
            <div class="order-meta">
              <span class="order-label">{{ $t('orderHistory.orderNo') }}:</span>
              <span class="order-value">{{ formatOrderNo(order.order_no) }}</span>
            </div>
            
            <!-- 第三行：金币数量 + 时间 -->
            <div class="order-meta">
              <span class="order-label">{{ $t('orderHistory.unitCoin') }}:</span>
              <span class="order-value">{{ order.amount }}</span>
              <span class="sep">•</span>
              <span class="order-value">{{ formatTime(order.createtime) }}</span>
            </div>
            
            <!-- 第四行：状态标签（右对齐） -->
            <div class="order-footer">
              <span class="status-tag" :class="getStatusClass(order.type)">
                {{ getStatusText(order.type) }}
              </span>
            </div>
          </div>
        </div>
        
        <!-- 加载更多提示 -->
        <div v-if="loading" class="load-tip">{{ $t('orderHistory.loading') }}</div>
        <div v-else-if="!hasMore && orderList.length > 0" class="load-tip">{{ $t('orderHistory.loadEnd') }}</div>
      </template>
      
      <!-- 空状态 -->
      <div v-else-if="!loading" class="empty-tip">{{ $t('orderHistory.empty') }}</div>
      
      <!-- 初始加载中的骨架（可选） -->
      <div v-if="loading && orderList.length === 0" class="loading-skeleton">
        <div class="skeleton-card" v-for="i in 3" :key="i"></div>
      </div>
    </div>
  </div>
</template>

<script>
import { orderList } from '@/apis/order';

export default {
  name: 'OrderHistory',
  data() {
    return {
      orderList: [],      // 订单列表（已映射前端所需字段）
      lastId: 0,          // 分页游标（最后一条记录的ID）
      pageSize: 10,
      hasMore: true,      // 是否还有更多数据
      loading: false,     // 是否加载中
    };
  },
  mounted() {
    this.checkLoginAndLoad();
    window.addEventListener('scroll', this.handleScroll);
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    // 检查登录状态
    checkLoginAndLoad() {
      const userInfo = this.$getUserInfo();
      if (!userInfo || !userInfo.id) {
        this.$router.push('/login');
        return;
      }
      this.refreshOrders();
    },

    // 强制刷新（重置所有分页状态）
    refreshOrders() {
      this.lastId = 0;
      this.hasMore = true;
      this.loadOrders(true);
    },

    // 获取订单列表（分页）
    loadOrders(isRefresh = false) {
      if (this.loading) return;
      if (!isRefresh && !this.hasMore) return;

      const userInfo = this.$getUserInfo();
      if (!userInfo || !userInfo.id) return;

      // 如果是刷新，确保游标重置（二次保障）
      if (isRefresh) {
        this.lastId = 0;
        this.hasMore = true;
      }

      this.loading = true;

      const params = {
        limit: this.pageSize,
        last_id: isRefresh ? 0 : this.lastId,
      };

      orderList(params)
        .then(res => {
          let records = res.data || [];

          if (isRefresh) {
            this.orderList = records;
          } else {
            this.orderList = this.orderList.concat(records);
          }

          // 更新分页游标和 hasMore
          if (records.length < this.pageSize) {
            this.hasMore = false;
          } else {
            this.hasMore = true;
            // 注意： records 中的 id 是原始后端的 id，映射后依然保留
            if (records.length > 0) {
              this.lastId = records[records.length - 1].id;
            }
          }
        })
        .catch(err => {
          console.error('获取订单列表失败', err);
          if (isRefresh) {
            this.orderList = [];
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },

    // 将时间戳转为 "YYYY/MM/DD HH:MM"
    formatTimestamp(timestamp) {
      if (!timestamp) return '';
      const date = new Date(timestamp * 1000);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}/${month}/${day} ${hours}:${minutes}`;
    },

    // 滚动加载更多（带简单防抖）
    handleScroll() {
      if (this.loading || !this.hasMore) return;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      if (scrollTop + windowHeight >= documentHeight - 100) {
        this.loadOrders(false);
      }
    },
    formatOrderNo(orderNo) {
      if (!orderNo) return '';
      return orderNo;
    //   if (orderNo.length <= 10) return orderNo;
    //   return orderNo.slice(0, 6) + '****' + orderNo.slice(-4);
    },

    // 格式化时间（兼容旧版，实际已用 formatTimestamp 预处理，此方法保留供模板调用）
    formatTime(timeStr) {
      if (!timeStr) return '';
      // 如果传入的是格式化后的字符串，直接返回
      if (typeof timeStr === 'string' && timeStr.includes('/')) return timeStr;
      // 兼容可能的时间戳
      return this.formatTimestamp(timeStr);
    },

    // 获取状态文本（多语言支持）
    getStatusText(status) {
      if(status==1){
        status = "failed";
      }else{
        status = "completed";
      }
      const map = this.$t('orderHistory.statusMap');
      return map[status] || (status === 2 ? '已完成' : '未支付');
    },
    // 获取状态样式类名
    getStatusClass(status) {
      if(status==1){
        status = "failed";
      }else{
        status = "completed";
      }
      return `status-${status}`;
    },

    goBack() {
      this.$router.back();
    },
  },
};
</script>

<style scoped>
.order-history-page {
  width: 100%;
  max-width: 450px;
  margin: 0 auto;
  background: #f5f5f7;
  color: #1d1d1f;
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", sans-serif;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  box-shadow: none;
}

/* 头部样式（与隐私政策页面一致） */
.header {
  display: flex;
  align-items: center;
  height: 49px;
  padding: 0 16px;
  background: rgba(245, 245, 247, 0.9);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  position: sticky;
  top: 0;
  z-index: 20;
  box-sizing: border-box;
  backdrop-filter: saturate(180%) blur(20px);
  -webkit-backdrop-filter: saturate(180%) blur(20px);
}
.back-btn {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  cursor: pointer;
  color: #007aff;
  transition: background 0.12s ease, transform 0.12s ease;
}
.back-btn:active {
  background: rgba(0, 122, 255, 0.1);
  transform: scale(0.96);
}
.back-icon {
  width: 11px;
  height: 11px;
  font-size: 0;
  border-left: 2px solid currentColor;
  border-bottom: 2px solid currentColor;
  transform: rotate(45deg);
}
.header-title {
  flex: 1;
  text-align: center;
  font-size: 17px;
  font-weight: 600;
  color: #1d1d1f;
  letter-spacing: 0;
  margin-right: 40px;
}

/* 主要内容区 */
.main-content {
  flex: 1;
  padding: 14px 12px 60px;
  overflow-y: auto;
}

/* 订单卡片 */
.order-card {
  background: #fff;
  border-radius: 16px;
  margin-bottom: 10px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.04);
  display: flex;
  overflow: hidden;
  transition: transform 0.12s ease, background 0.12s ease;
}
.order-card:active {
  transform: scale(0.99);
  background: #f9f9fb;
}

/* 左侧状态装饰条 */
.order-status-bar {
  width: 4px;
  background: #007aff;
}
.order-status-bar.status-completed {
  background: #34c759;
}
.order-status-bar.status-pending {
  background: #ff9500;
}
.order-status-bar.status-cancelled,
.order-status-bar.status-failed {
  background: #8e8e93;
}

/* 订单信息区域 */
.order-info {
  flex: 1;
  min-width: 0;
  padding: 14px 14px 13px;
}

.order-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 9px;
}
.product-name {
  font-size: 16px;
  font-weight: 600;
  color: #1d1d1f;
}
.order-amount {
  font-size: 17px;
  font-weight: 700;
  color: #1d1d1f;
}
.currency-symbol {
  font-size: 14px;
  margin-right: 2px;
}

.order-meta {
  font-size: 12px;
  color: #8e8e93;
  margin-bottom: 7px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}
.order-label {
  margin-right: 6px;
  color: #8e8e93;
}
.order-value {
  color: #3a3a3c;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sep {
  margin: 0 6px;
  color: #c7c7cc;
}

.order-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 9px;
}
.status-tag {
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 999px;
  background: #e8f2ff;
  color: #007aff;
}
.status-tag.status-completed {
  background: #e8f7ee;
  color: #248a3d;
}
.status-tag.status-pending {
  background: #fff4e5;
  color: #c26100;
}
.status-tag.status-cancelled,
.status-tag.status-failed {
  background: #f2f2f7;
  color: #8e8e93;
}

/* 加载提示 */
.load-tip {
  text-align: center;
  padding: 16px;
  font-size: 13px;
  font-weight: 500;
  color: #8e8e93;
}

/* 空状态样式 */
.empty-tip {
  width: 100%;
  text-align: center;
  padding: 64px 16px;
  font-size: 13px;
  font-weight: 500;
  color: #8e8e93;
  box-sizing: border-box;
}

/* 骨架屏加载效果 */
.loading-skeleton {
  padding: 0;
}
.skeleton-card {
  background: linear-gradient(100deg, #e5e5ea 30%, #f2f2f7 50%, #e5e5ea 70%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  height: 108px;
  border-radius: 16px;
  margin-bottom: 10px;
}
@keyframes loading {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
</style>
